<template>
  <div>
    <div class="d-flex align-center py-3">
      <h2 class="flex-grow-1">{{ title }}</h2>
      <v-btn color="primary" small @click="openDialog()">Add</v-btn>
    </div>

    <!-- Table -->
    <v-data-table :headers="headers" :items="items" item-value="id" class="elevation-1" hide-default-footer>
      <!-- Banner Image -->
      <template #[`item.bannerUrl`]="{ item }">
        <v-avatar size="40">
          <v-img :src="item.bannerUrl" />
        </v-avatar>
      </template>

      <!-- Actions -->
      <template #[`item.actions`]="{ item }">
        <v-btn icon small color="primary" @click="openDialog(item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Form Modal -->
    <v-dialog v-model="dialog" max-width="700px">
      <v-card>
        <v-card-title>
          {{ Form.id ? 'Edit Result' : 'Add Result' }}
          <v-spacer />
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-form ref="formRef" v-model="Form.valid" lazy-validation>
          <v-card-text>
            <v-row v-for="field in formFields" :key="field.key" class="mt-3">
              <v-col cols="12" md="6" v-for="lang in languages" :key="lang" class="py-0">
                <component
                  :is="field.type"
                  v-model="Form[field.key][lang]"
                  :label="`${field.label} ${lang.toUpperCase()}`"
                  dense
                  outlined
                  :rules="[requiredRule]"
                  v-bind="field.props"
                />
              </v-col>
            </v-row>

            <v-row v-if="type === 'result'">
              <v-col cols="12" class="pt-0">
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="Form.uiConfig.ctaUrl"
                    label="CTA Url"
                    dense
                    outlined
                    hide-details
                    class="flex-grow-1"
                  />
                </div>
              </v-col>
            </v-row>

            <!-- UI Config -->
            <v-row>
              <v-col v-if="type == 'result'" cols="6">
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="Form.uiConfig.backgroundColorStart"
                    label="Background Color Start"
                    dense
                    outlined
                    hide-details
                    class="flex-grow-1"
                  />
                  <div
                    class="block-color"
                    :style="{ backgroundColor: Form.uiConfig.backgroundColorStart || '#fff' }"
                  ></div>
                </div>
              </v-col>
              <v-col v-if="type == 'result'" cols="6">
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="Form.uiConfig.backgroundColorEnd"
                    label="Background Color End"
                    dense
                    outlined
                    hide-details
                    class="flex-grow-1"
                  />
                  <div
                    class="block-color"
                    :style="{ backgroundColor: Form.uiConfig.backgroundColorEnd || '#fff' }"
                  ></div>
                </div>
              </v-col>
              <v-col v-if="type == 'landing'" cols="6">
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="Form.uiConfig.backgroundColor"
                    label="Background Color"
                    dense
                    outlined
                    hide-details
                    class="flex-grow-1"
                  />
                  <div class="block-color" :style="{ backgroundColor: Form.uiConfig.backgroundColor || '#fff' }"></div>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="Form.uiConfig.ctaColor"
                    label="CTA Color"
                    dense
                    outlined
                    hide-details
                    class="flex-grow-1"
                  />
                  <div class="block-color" :style="{ backgroundColor: Form.uiConfig.ctaColor || '#fff' }"></div>
                </div>
              </v-col>
            </v-row>

            <!-- Banner -->
            <div class="d-flex flex-column align-center mt-4">
              <v-img :src="selectBannerBase64 || Form.bannerUrl" max-width="150" class="mb-2 image-border" />
              <v-btn small color="secondary" @click="selectBanner">Change Banner</v-btn>
              <v-input
                :value="selectBannerBase64 || Form.bannerUrl"
                :rules="[imageRequiredRule]"
                hide-details="auto"
                class="pa-0"
              />
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" :disabled="isSubmitDisabled" @click="onSubmit">
              {{ Form.id ? 'Save' : 'Add' }}
            </v-btn>
            <v-btn v-if="Form.id" text color="error" @click="deleteResult(Form.id)"> Delete </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <input type="file" ref="bannerInput" style="display: none" accept="image/*" @change="onSelectBanner" />
  </div>
</template>

<script>
import Form from '../../../form/form';
import { srcToBase64 } from '../../../utils/stringUtil';
export default {
  props: {
    title: { type: String, required: true },
    type: { type: String, required: true },
    items: { type: Array, required: true },
    assessmentId: { type: Number, required: true },
  },
  data() {
    return {
      dialog: false,
      requiredRule: (v) => (v && v.toString().trim().length > 0) || 'Required',
      imageRequiredRule: (v) => !!v || 'Required',
      languages: ['th', 'en'],
      formFields: [
        { key: 'title', label: 'Title', type: 'v-text-field' },
        { key: 'pretitle', label: 'Pretitle', type: 'v-text-field' },
        { key: 'description', label: 'Description', type: 'v-textarea', props: { rows: 2 } },
        { key: 'note', label: 'Note', type: 'v-text-field' },
        { key: 'startButton', label: 'Start Button', type: 'v-text-field' },
      ],
      headers: [
        { text: 'ID', value: 'id', align: 'center' },
        { text: 'Title', value: 'title[th]' },
        { text: 'Pretitle', value: 'pretitle[th]' },
        { text: 'Description', value: 'description[th]' },
        { text: 'Note', value: 'note[th]' },
        { text: 'Level', value: 'level' },
        { text: 'Banner', value: 'bannerUrl', align: 'center' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      Form: new Form({
        data() {
          return {
            id: null,
            title: { th: '', en: '' },
            pretitle: { th: '', en: '' },
            description: { th: '', en: '' },
            note: { th: '', en: '' },
            startButton: { th: '', en: '' },
            uiConfig: {
              ctaColor: '',
              backgroundColor: '',
              backgroundColorStart: '',
              backgroundColorEnd: '',
              ctaUrl: '',
            },
            valid: false,
          };
        },
      }),
      selectBannerBase64: null,
    };
  },
  methods: {
    openDialog(page = null) {
      if (page) {
        this.Form = JSON.parse(JSON.stringify(page));
        this.Form.id = page.id;
      } else {
        this.Form = {
          title: { th: '', en: '' },
          pretitle: { th: '', en: '' },
          description: { th: '', en: '' },
          note: { th: '', en: '' },
          startButton: { th: '', en: '' },
          uiConfig: { ctaColor: '', backgroundColor: '', backgroundColorStart: '', backgroundColorEnd: '', ctaUrl: '' },
          valid: false,
        };
      }
      this.dialog = true;
      this.selectBannerBase64 = null;
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.resetValidation();
      });
    },
    closeDialog() {
      this.dialog = false;
    },
    async onSubmit() {
      const isValid = this.$refs.formRef.validate();
      if (!isValid) return;

      const body = {
        title: this.Form.title,
        pretitle: this.Form.pretitle,
        description: this.Form.description,
        type: this.type,
        note: this.Form.note,
        startButton: this.Form.startButton,
        uiConfig: this.Form.uiConfig,
        assessmentId: this.assessmentId,
      };

      if (this.selectBannerBase64) {
        body.banner = {
          fileName: this.selectImageFileName,
          base64: srcToBase64(this.selectBannerBase64),
        };
      }

      if (this.Form.id) {
        this.$emit('onUpdate', this.Form.id, body);
      } else {
        this.$emit('onAdd', body);
      }

      this.dialog = false;
    },
    deleteResult(id) {
      this.$emit('onDelete', id);
      this.dialog = false;
    },
    selectBanner() {
      this.$refs.bannerInput.click();
    },
    onSelectBanner(e) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.selectImageFileName = file.name;
        this.selectBannerBase64 = ev.target.result;
      };
      reader.readAsDataURL(file);
    },
  },
  computed: {
    isSubmitDisabled() {
      const requiredFields = ['title', 'pretitle', 'description', 'note', 'startButton'];

      for (const key of requiredFields) {
        for (const lang of this.languages) {
          const value = (this.Form[key]?.[lang] || '').trim();
          if (!value) return true;
        }
      }

      if (!this.selectBannerBase64 && !this.Form.bannerUrl) return true;

      return false;
    },
  },
};
</script>

<style scoped>
.image-border {
  border: 1px dashed grey;
  border-radius: 8px;
}

.block-color {
  width: 20px;
  height: 20px;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-left: 6px;
}
</style>
